import { useState, useEffect, useRef } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface Conversation {
  id: string
  title: string
  message_count: number
  updated_at: string
}

interface Prompt {
  prompt: string
  category: string
}

export default function AIChatPage() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [prompts, setPrompts] = useState<Prompt[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchConversations()
    fetchPrompts()
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const fetchConversations = async () => {
    const res = await fetch(API_BASE + '/chat/conversations?user_id=demo_user')
    if (res.ok) {
      const data = await res.json()
      setConversations(data.conversations || [])
    }
  }

  const fetchPrompts = async () => {
    const res = await fetch(API_BASE + '/chat/prompts')
    if (res.ok) {
      const data = await res.json()
      setPrompts(data.prompts || [])
    }
  }

  const createConversation = async () => {
    const res = await fetch(API_BASE + '/chat/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: 'demo_user' })
    })
    if (res.ok) {
      const conv = await res.json()
      setCurrentConversation(conv.id)
      setMessages([])
      fetchConversations()
      return conv.id
    }
    return null
  }

  const selectConversation = async (convId: string) => {
    setCurrentConversation(convId)
    const res = await fetch(API_BASE + '/chat/conversations/' + convId)
    if (res.ok) {
      const data = await res.json()
      setMessages(data.messages || [])
    }
  }

  const sendMessage = async () => {
    if (!input.trim() || !currentConversation) return
    setLoading(true)
    const res = await fetch(API_BASE + '/chat/conversations/' + currentConversation + '/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: input })
    })
    if (res.ok) {
      const data = await res.json()
      setMessages(prev => [...prev, data.user_message, data.assistant_message])
      setInput('')
      fetchConversations()
    }
    setLoading(false)
  }

  const usePrompt = async (prompt: string) => {
    let convId = currentConversation
    if (!convId) {
      convId = await createConversation()
    }
    setInput(prompt)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">AI Chat Assistant</h1>
        <div className="grid grid-cols-4 gap-6">
          <div className="col-span-1 bg-white rounded-lg shadow p-4">
            <button onClick={createConversation} className="w-full py-2 bg-blue-600 text-white rounded mb-4 hover:bg-blue-700">+ New Chat</button>
            <div className="space-y-2">
              {conversations.map(conv => (
                <div key={conv.id} onClick={() => selectConversation(conv.id)} className={'p-3 rounded cursor-pointer ' + (currentConversation === conv.id ? 'bg-blue-100' : 'hover:bg-gray-100')}>
                  <div className="font-medium truncate">{conv.title}</div>
                  <div className="text-xs text-gray-500">{conv.message_count} messages</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-3 bg-white rounded-lg shadow flex flex-col h-[600px]">
            {currentConversation ? (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map(msg => (
                    <div key={msg.id} className={'flex ' + (msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                      <div className={'max-w-[70%] p-3 rounded-lg ' + (msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100')}>
                        <pre className="whitespace-pre-wrap font-sans text-sm">{msg.content}</pre>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} placeholder="Ask about your data..." className="flex-1 px-4 py-2 border rounded-lg" disabled={loading} />
                    <button onClick={sendMessage} disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? '...' : 'Send'}</button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <h2 className="text-2xl font-semibold mb-4">Start a conversation</h2>
                <p className="text-gray-500 mb-6">Ask questions about your sales, markets, inventory, and more</p>
                <div className="grid grid-cols-2 gap-3 w-full max-w-lg">
                  {prompts.slice(0, 6).map(p => (
                    <button key={p.prompt} onClick={() => usePrompt(p.prompt)} className="p-3 text-left border rounded-lg hover:bg-blue-50 hover:border-blue-300">
                      <div className="text-xs text-blue-600 mb-1">{p.category}</div>
                      <div className="text-sm">{p.prompt}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
